<template>
  <div id="dividends-by-position">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1 pb-2">
        <h1 class="card-title mb-1">
          {{ $t(`views.dashboardDetail.dividends.dividendsByPosition.title`) }}
        </h1>
        <v-spacer />
        <ExportButton
          class="mt-1"
          :click="downloadCsv"
          :loading="exportLoading"
          :disabled="!summary.length"
        />
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="showChart && loadedData"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="dividends-by-position-table"
            :headers="headers"
            :items="summary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="itemsPerPage"
            :footer-props="{
              'items-per-page-options': [5, 10, 15, 20, 30, 50, 100],
            }"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <td class="text-right">
                  <currency :value="totalRow.totalBaseCurrency" :currency="user.baseCurrency" />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.originRetentionBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                </td>
                <td class="text-right">
                  <currency :value="totalRow.netoBaseCurrency" :currency="user.baseCurrency" />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.destinationRetentionBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                </td>
                <td class="text-right">
                  <currency :value="totalRow.totalNetoBaseCurrency" :currency="user.baseCurrency" />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.retentionReturnedBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.netoWithReturnBaseCurrency"
                    :currency="user.baseCurrency"
                    strong
                  />
                </td>
              </tr>
            </template>
            <template v-slot:item.position.ticker="{ item }">
              <div class="items-flex">
                <TableTicker :position="item.position" />
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon
                      :text="$t(`views.dashboardDetail.dividends.dividendsByPosition.warning`)"
                    />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.totalBaseCurrency="{ item }">
              <currency
                v-if="item.totalBaseCurrency"
                :value="item.totalBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.retentionReturnedBaseCurrency="{ item }">
              <currency
                v-if="item.retentionReturnedBaseCurrency"
                :value="item.retentionReturnedBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.originRetentionBaseCurrency="{ item }">
              <currency
                v-if="item.originRetentionBaseCurrency"
                :value="item.originRetentionBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.netoBaseCurrency="{ item }">
              <currency
                v-if="item.netoBaseCurrency"
                :value="item.netoBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
              <currency
                v-if="item.destinationRetentionBaseCurrency"
                :value="item.destinationRetentionBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.totalNetoBaseCurrency="{ item }">
              <currency
                v-if="item.totalNetoBaseCurrency"
                :value="item.totalNetoBaseCurrency"
                :currency="user.baseCurrency"
              />
            </template>
            <template v-slot:item.netoWithReturnBaseCurrency="{ item }">
              <currency
                v-if="item.netoWithReturnBaseCurrency"
                :value="item.netoWithReturnBaseCurrency"
                :currency="user.baseCurrency"
                strong
              />
              <span v-else></span>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getDividendSummary, getDividendSummaryCsv } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import BarChart from '@/views/components/core/chartjs/BarChart'
import TableTicker from '@/views/components/common/TableTicker'
import BarLoading from '@/views/components/core/BarLoading'
import Currency from '@/views/components/common/Currency'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: { TableTicker, BarChart, BarLoading, Currency, WarningIcon, ExportButton },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
    showChart: {
      type: Boolean,
      default: true,
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    summary: [],
    exportLoading: false,
    totalRow: null,
    currentPage: null,
    loadedData: false,
    barChartData: {
      labels: null,
      datasets: null,
    },
    filters: [],
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    chartData() {
      return {
        labels: map(this.summary, (item) => {
          if (['fund'].indexOf(item.position.type) > -1 && item.position.name)
            return item.position.name
          return item.position.ticker
        }),
        series: [map(this.summary, 'netoWithReturnBaseCurrency')],
      }
    },
    headers() {
      return [
        {
          text: this.$t(`views.dividends.dividendTable.position`),
          value: 'position.ticker',
          divider: true,
        },
        {
          text: `${this.$t('views.dividends.dividendTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.totalOriginRetention'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'originRetentionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.neto')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'netoBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dividends.dividendTable.destinationRetention'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.totalNeto')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalNetoBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.retentionReturned')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'retentionReturnedBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.dividends.dividendTable.netoWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'netoWithReturnBaseCurrency',
          align: 'right',
        },
      ]
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return item.exchangeRates && item.exchangeRates.some((elem) => elem === null)
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    createBarChartObject(data) {
      this.barChartData.datasets = [
        {
          label: `${this.$t('views.dividends.dividendTable.netoWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          data: map(data, 'netoWithReturnBaseCurrency'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarBlue
            : DEFAULT_PALETTE.chartsBarBlue,
        },
      ]
      this.barChartData.labels = map(data, (item) => {
        if (['fund'].indexOf(item.position.type) > -1 && item.position.name)
          return item.position.name
        return item.position.ticker
      })
    },
    async loadSummary(filters) {
      this.loadedData = false
      this.filters = filters
      try {
        let summary = await getDividendSummary({
          group: 'byPosition',
          filter: {
            ...filters,
          },
        })
        summary = summary.data

        this.totalRow = summary.shift()
        this.summary = summary
        this.currentPage = 1

        this.createBarChartObject(summary)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
    async downloadCsv() {
      this.exportLoading = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await getDividendSummaryCsv({
          group: 'byPosition',
          filter: {
            ...this.filters,
          },
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(
          blob,
          `Filios - ${this.$t(
            `views.dashboardDetail.dividends.dividendsByPosition.title`
          )} ${nowFormatted}.csv`
        )
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#dividends-by-position {
  #dividends-by-position-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
